import React, { useEffect, useRef, useState } from "react";

import { CropDataCompleteness, formatNumericCurrency, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { CROP_TYPE_FCPO, FieldCropRecommendation } from "models";

import {
  Card,
  Flex,
  Icon,
  LoadingOverlay,
  MonthlyCropCard,
  MonthlyCropCardProps,
  MonthlyEmptyCropCardProps,
  Popover,
  TooltipWrapper,
  YearlyCropCard,
  YearlyCropCardProps,
} from "components";

import { CropRecommendationCardForm } from "pages/app/field-timeline/components/CropRecommendationCardForm";

export type PracticeBundleOpts = {
  slug: string;
  hasMissingData: boolean;
};

export type CropRotationCardProps = {
  cropId: string;
  cropType: CROP_TYPE_FCPO;
  cropName?: string;
  hasResidue?: boolean;
  payout?: number;
  currency?: CurrencyCode;
  year?: number;
  recommendations?: {
    fieldId: string;
    harvestEndDate: string;
    seedingStartDate: string;
    cropRecommendations: FieldCropRecommendation[];
  };
  dataTestId?: string;
  onClick?: () => void;
  activePracticeBundles?: Array<PracticeBundleOpts>;
  isMandatory?: boolean;
  isBaseline?: boolean;
  isConfirmed?: boolean;
  cardViewType?: TIMELINE_VIEW;
  isEmptyCard?: boolean;
  isSelected?: boolean;
  isEmptyWithNoEnrollment?: boolean;
  isReportingYear?: boolean;
  enrolledInInventoryProgram?: boolean;
  cropData?: CropDataCompleteness;
  simulationId?: string;
  fieldId?: string;
  fieldSize?: number;
  cropMixId?: string;
  openTasksCount?: number;
  isFlaggedByVerifier?: boolean;
  // Multi-selection props
  isChecked?: boolean;
  isInactive?: boolean;
  // TODO (Alp): Performance optimization: Pass fetching state from parent instead of subscribing in every card
  isFetching?: boolean;
};

export enum TIMELINE_VIEW {
  MONTHLY_CROP_CARD = "monthlyCropCard",
  YEARLY_CROP_CARD = "yearlyCropCard",
}

export const CropRotationRecommendationCard = ({
  cropType,
  payout,
  year,
  recommendations,
  isSelected,
  dataTestId,
  className,
  simulationId,
  fieldSize,
  currency,
  isInactive = false,
}: {
  cropType: CROP_TYPE_FCPO;
  payout?: number;
  currency?: CurrencyCode;
  year?: number;
  recommendations?: {
    fieldId: string;
    harvestEndDate: string;
    seedingStartDate: string;
    cropRecommendations: FieldCropRecommendation[];
  };
  isSelected?: boolean;
  dataTestId?: string;
  className?: string;
  simulationId?: string;
  fieldSize?: number;
  isInactive?: boolean;
}) => {
  const { t } = useTranslation("fieldTimeline");
  const [shouldShowTextTooltip, setShouldShowTextTooltip] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const nameContainerRef = useRef<HTMLParagraphElement | null>(null);
  const nameRef = useRef<HTMLParagraphElement | null>(null);

  const isCoverCrop = cropType === CROP_TYPE_FCPO.COVER;

  useEffect(() => {
    const nameContainerElement = nameContainerRef.current;
    const nameElement = nameRef.current;
    if (!nameContainerElement || !nameElement) return;

    const handleResize = () => {
      setShouldShowTextTooltip(nameContainerElement!.offsetWidth < nameElement!.offsetWidth);
    };

    const observer = new ResizeObserver(handleResize);

    const cardElements = [nameContainerElement, nameElement];
    // start the observer of each card ref
    cardElements.forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const payoutString = payout
    ? `${t("cropCard.upTo")} ${
        simulationId && fieldSize
          ? `${formatNumericCurrency(payout / fieldSize, { decimals: 0, currency })}/ha`
          : formatNumericCurrency(payout, { decimals: 0, currency })
      }`
    : "";

  return (
    <Popover
      className="crop-recommendation-card-popover"
      content={
        recommendations && (
          <CropRecommendationCardForm
            harvestEndDate={recommendations.harvestEndDate}
            seedingStartDate={recommendations.seedingStartDate}
            cropType={cropType}
            recommendations={recommendations.cropRecommendations}
            year={year}
            fieldId={recommendations.fieldId}
            simulationId={simulationId}
            fieldSize={fieldSize}
            onClose={() => setShowModal(false)}
          />
        )
      }
      trigger={isInactive ? undefined : "click"}
      placement="left"
      open={isInactive || isCoverCrop ? false : showModal}
      onOpenChange={!isInactive ? () => setShowModal(!showModal) : undefined}
      destroyOnHidden={true}
    >
      <Card
        className={`recommendation-crop-empty-card layout ${isSelected ? "selected" : isInactive ? "inactive-card" : ""} ${
          isCoverCrop ? "cover-crop" : "undersowing-crop"
        } ${className ?? ""}`}
        onClick={!isInactive ? () => setShowModal(!showModal) : undefined}
        data-testid={`side-crop-empty-card-${dataTestId}`}
      >
        <TooltipWrapper
          // avoid having the tooltip open when the modal is open
          open={showModal ? false : undefined}
          tooltip={
            isCoverCrop
              ? {
                  title: t("cropCard.cmcRecommendationCoverCropTooltip"),
                  placement: "top",
                }
              : shouldShowTextTooltip
              ? {
                  title: `${t(isCoverCrop ? "cropCard.recommendationCoverCrop" : "cropCard.recommendationInterseeding")} ${payoutString}`,
                  placement: "top",
                }
              : {}
          }
        >
          <Flex className="payout-content">
            <Flex className="icon-container">
              <Icon
                dataTestId={`side-crop-empty-card-${dataTestId}-bulb-icon ${isCoverCrop ? "cover-crop" : ""}`}
                className="recommendation-card-icon"
                iconName={isCoverCrop ? "klim/plant-ai" : "tabler/bulb"}
              />
            </Flex>
            <p ref={nameContainerRef} className="payout-text-container">
              <span ref={nameRef} className="payout-text">
                {`${
                  isCoverCrop
                    ? t("cropCard.cmcRecommendationCoverCrop")
                    : `${t(isCoverCrop ? "cropCard.recommendationCoverCrop" : "cropCard.recommendationInterseeding")} ${payoutString}`
                }`}
              </span>
            </p>
          </Flex>
        </TooltipWrapper>
      </Card>
    </Popover>
  );
};

export const getCardContentFactory = (props: MonthlyEmptyCropCardProps | YearlyCropCardProps, cardViewType: TIMELINE_VIEW) => {
  const cardContentMap = {
    [TIMELINE_VIEW.MONTHLY_CROP_CARD]: <MonthlyCropCard {...(props as MonthlyCropCardProps)} />,
    [TIMELINE_VIEW.YEARLY_CROP_CARD]: <YearlyCropCard {...(props as YearlyCropCardProps)} />,
  };

  return cardContentMap[cardViewType] ?? cardContentMap[TIMELINE_VIEW.MONTHLY_CROP_CARD];
};

export const CropRotationCard = React.memo(
  ({
    cropId,
    cropName,
    hasResidue,
    payout,
    currency,
    cropType,
    onClick,
    dataTestId,
    activePracticeBundles = [],
    cardViewType = TIMELINE_VIEW.MONTHLY_CROP_CARD,
    isMandatory,
    isBaseline,
    isConfirmed,
    isEmptyCard,
    recommendations,
    isSelected = false,
    isEmptyWithNoEnrollment = false,
    isReportingYear = false,
    enrolledInInventoryProgram = false,
    simulationId,
    fieldId,
    fieldSize,
    cropMixId,
    isChecked = false,
    isInactive = false,
    year,
    openTasksCount,
    isFlaggedByVerifier = false,
    isFetching = false,
  }: CropRotationCardProps) => {
    const [shouldShowNameTooltip, setShouldShowCropNameTooltip] = useState<boolean>(false);
    const [shouldHideIcons, setShouldHideIcons] = useState<boolean>(false);
    const [shouldShowEuroIcon, setShouldShowEuroIcon] = useState(false);
    const [shouldHideBaselineTag, setShouldHideBaselineTag] = useState(false);
    const [isNarrow, setIsNarrow] = useState(false);

    const cardRef = useRef<HTMLDivElement | null>(null);
    const nameContainerRef = useRef<HTMLParagraphElement | null>(null);
    const nameRef = useRef<HTMLParagraphElement | null>(null);

    useEffect(() => {
      const nameContainerElement = nameContainerRef.current;
      const nameElement = nameRef.current;
      const cardElement = cardRef.current;
      if (!nameContainerElement || !nameElement || !cardElement || !cardElement) return;

      const handleResize = () => {
        setShouldShowCropNameTooltip(nameContainerElement!.offsetWidth < nameElement!.offsetWidth);
        setShouldShowEuroIcon(cardElement.offsetWidth < 230);
        setShouldHideBaselineTag(cardElement.offsetWidth < 175);
        setShouldHideIcons(cardViewType === TIMELINE_VIEW.MONTHLY_CROP_CARD ? cardElement.offsetWidth < 115 : cardElement.offsetWidth < 80);
        setIsNarrow(cardElement.offsetWidth < 80);
      };

      const observer = new ResizeObserver(handleResize);

      const cardElements = [cardElement, nameContainerElement, nameElement];
      // start the observer of each card ref
      cardElements.forEach((element) => {
        if (element) observer.observe(element);
      });

      return () => observer.disconnect();
    }, [cardViewType]);

    const Card = getCardContentFactory(
      {
        cropId,
        cropName,
        fieldId,
        hasResidue,
        payout,
        currency,
        onClick,
        dataTestId,
        isSelected,
        isNarrow,
        nameContainerRef,
        nameRef,
        shouldShowNameTooltip,
        shouldHideIcons,
        shouldShowEuroIcon,
        shouldHideBaselineTag,
        cropType,
        activePracticeBundles,
        isMandatory,
        isEmptyCard,
        recommendations,
        isEmptyWithNoEnrollment,
        isBaseline,
        isConfirmed,
        isFetching,
        isReportingYear,
        enrolledInInventoryProgram,
        simulationId,
        fieldSize,
        cropMixId,
        isChecked,
        isInactive,
        year,
        openTasksCount,
        isFlaggedByVerifier,
      },
      cardViewType
    );

    return <div ref={cardRef}>{isFetching ? <LoadingOverlay>{Card}</LoadingOverlay> : Card}</div>;
  }
);
